import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';

import { ButtonTypes } from 'constants/Constants';

import Icon from 'components/common/Icon/Icon';
import ToolTip from 'components/common/ToolTip/ToolTip';

import styles from './InlineButton.module.scss';

const { PRIMARY, SECONDARY, SMALL } = ButtonTypes;

const s = classNames.bind(styles);

const InlineButton = ({
	className,
	text,
	style,
	size,
	textClassName,
	icon,
	children,
	iconRight,
	iconClassName,
	rotateIcon,
	color,
	onClickCallback,
	to,
	separator,
	separatorLeft,
	wordWrap,
	wordBreak,
	withAnimation = true,
	withoutTextOnSmall,
	disabled,
	toolTip
}) => {
	const getClassNames = () =>
		s('container', {
			container_primary: color === PRIMARY,
			container_secondary: color === SECONDARY,
			container_small: size === SMALL, // Will affect font size
			container_withSeparator: separator,
			container_withAnimation: withAnimation,
			container_withSeparatorLeft: separatorLeft,
			container_withIconRight: iconRight,
			container_disabled: disabled,
			[className]: className
		});

	/**
	|--------------------------------------------------
	| @function getInnerMarkup
	|--------------------------------------------------
	*/
	const getInnerMarkup = () => (
		<React.Fragment>
			{icon ? (
				<Icon icon={icon} className={s('icon', { [iconClassName]: iconClassName })} rotateIcon={rotateIcon} fixedIconColor color={color} />
			) : null}
			<span
				className={s('text', {
					text_withIconLeft: icon && text && !iconRight,
					text_withIconRight: icon && text && iconRight,
					text_wordWrap: wordWrap,
					text_wordBreak: wordBreak,
					text_withoutTextOnSmall: withoutTextOnSmall,
					[textClassName]: textClassName
				})}
			>
				{text}
				{children}
			</span>
		</React.Fragment>
	);

	/**
	|--------------------------------------------------
	| @function getButton
	|--------------------------------------------------
	*/
	const getButton = () => {
		if (to) {
			return (
				<Link className={getClassNames()} to={to}>
					{getInnerMarkup()}
				</Link>
			);
		}

		if (onClickCallback) {
			return (
				<button className={getClassNames()} onClick={onClickCallback} style={style}>
					{getInnerMarkup()}
				</button>
			);
		}

		return null;
	};

	return toolTip ? (
		<ToolTip text={toolTip} toolTipClassName={s({ toolTipWithSeparator: separator })}>
			{getButton()}
		</ToolTip>
	) : (
		getButton()
	);
};

InlineButton.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	icon: PropTypes.string,
	rotateIcon: PropTypes.bool,
	iconClassName: PropTypes.string,
	iconRight: PropTypes.bool,
	color: PropTypes.string,
	size: PropTypes.string,
	textClassName: PropTypes.string,
	separator: PropTypes.bool,
	separatorLeft: PropTypes.bool,
	wordWrap: PropTypes.bool,
	wordBreak: PropTypes.bool,
	withoutTextOnSmall: PropTypes.bool,
	withAnimation: PropTypes.bool,
	to: PropTypes.string,
	disabled: PropTypes.bool,
	onClickCallback: PropTypes.func,
	style: PropTypes.object,
	text: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	toolTip: PropTypes.string
};

export default memo(InlineButton);
